interface ConsoleLog {
  timestamp: string;
  type: 'log' | 'info' | 'warn' | 'error';
  args: any[];
}

interface ErrorObject {
  errorType: string;
  errorMessage: string;
  stack?: string;
  [key: string]: any;
}

declare global {
  interface Window {
    __consoleLogs: ConsoleLog[];
  }
}

const MAX_LOGS = 1000;

// Try to get existing console logs from the browser's console API
function getExistingConsoleLogs(): ConsoleLog[] {
  if (typeof window === 'undefined' || typeof console === 'undefined') {
    return [];
  }

  try {
    // Try to get logs from the browser's console API if available
    if ('getEntries' in console) {
      return (console as any).getEntries().map((entry: any) => ({
        timestamp: new Date(entry.timestamp).toISOString(),
        type: entry.type,
        args: [entry.message]
      }));
    }
  } catch (e) {
    // Ignore errors if console API is not available
  }
  return [];
}

export function setupConsoleCapture() {
  if (typeof window === 'undefined' || typeof console === 'undefined') {
    return () => {}; // Return no-op cleanup if window/console not available
  }

  // Initialize with any existing logs
  if (!window.__consoleLogs) {
    window.__consoleLogs = getExistingConsoleLogs();
  }

  // Store original console methods
  const originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  };

  // Helper to add log to our storage
  const addLog = (type: ConsoleLog['type'], args: any[]) => {
    if (!window.__consoleLogs) {
      window.__consoleLogs = [];
    }

    const log: ConsoleLog = {
      timestamp: new Date().toISOString(),
      type,
      args: args.map(arg => {
        try {
          // Handle Error objects
          if (arg instanceof Error) {
            const errorObj: ErrorObject = {
              errorType: arg.name,
              errorMessage: arg.message,
              stack: arg.stack
            };
            // Add any custom properties
            for (const key in arg) {
              if (Object.prototype.hasOwnProperty.call(arg, key)) {
                errorObj[key] = (arg as any)[key];
              }
            }
            return errorObj;
          }

          // Handle DOM elements
          if (arg instanceof Element) {
            return `[${arg.tagName.toLowerCase()}${arg.id ? `#${arg.id}` : ''}${
              arg.className ? `.${arg.className.replace(/\s+/g, '.')}` : ''
            }]`;
          }

          // Handle other objects
          return JSON.parse(JSON.stringify(arg, (key, value) => {
            if (value === window) return '[Window]';
            if (value === document) return '[Document]';
            if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
            if (value instanceof Element) return `[${value.tagName.toLowerCase()}]`;
            if (value instanceof Error) {
              return {
                errorType: value.name,
                errorMessage: value.message,
                stack: value.stack
              };
            }
            return value;
          }));
        } catch (e) {
          // If JSON.stringify fails, convert to string
          try {
            return String(arg);
          } catch (stringError) {
            return '[Unable to convert to string]';
          }
        }
      })
    };

    window.__consoleLogs.push(log);

    // Keep only the last MAX_LOGS entries
    if (window.__consoleLogs.length > MAX_LOGS) {
      window.__consoleLogs = window.__consoleLogs.slice(-MAX_LOGS);
    }
  };

  // Override console methods
  console.log = function(...args: any[]) {
    addLog('log', args);
    originalConsole.log.apply(console, args);
  };

  console.info = function(...args: any[]) {
    addLog('info', args);
    originalConsole.info.apply(console, args);
  };

  console.warn = function(...args: any[]) {
    addLog('warn', args);
    originalConsole.warn.apply(console, args);
  };

  console.error = function(...args: any[]) {
    addLog('error', args);
    originalConsole.error.apply(console, args);
  };

  // Also try to capture unhandled errors and rejections
  const errorHandler = (event: ErrorEvent) => {
    addLog('error', [{
      errorType: 'Uncaught Error',
      errorMessage: event.message,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    }]);
  };

  const rejectionHandler = (event: PromiseRejectionEvent) => {
    addLog('error', [{
      errorType: 'Unhandled Promise Rejection',
      errorMessage: event.reason?.message || String(event.reason),
      stack: event.reason?.stack
    }]);
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  // Return cleanup function
  return () => {
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    if (window.__consoleLogs) {
      window.__consoleLogs = [];
    }
  };
} 